
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Wand2, Loader2, Copy, CheckCircle } from 'lucide-react';
import { useAIContentGeneration } from '@/hooks/useAIContentGeneration';
import { toast } from 'sonner';

interface AIBlogGeneratorProps {
  onContentGenerated: (title: string, content: string) => void;
}

const AIBlogGenerator: React.FC<AIBlogGeneratorProps> = ({ onContentGenerated }) => {
  const [prompt, setPrompt] = useState('');
  const [gameTitle, setGameTitle] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [copied, setCopied] = useState(false);
  const { generateBlogPost, generateCustomContent, isLoading } = useAIContentGeneration();

  const handleGenerateFromGame = async () => {
    if (!gameTitle.trim()) {
      toast.error('Please enter a game title');
      return;
    }

    try {
      console.log('Generating blog post for game:', gameTitle);
      const content = await generateBlogPost({
        title: gameTitle,
        category: 'gaming',
        description: `Generate a blog post about the game ${gameTitle}`,
      });
      
      console.log('Generated content:', content);
      setGeneratedContent(content);
      
      // Extract title and content from generated text
      const lines = content.split('\n').filter(line => line.trim());
      let extractedTitle = '';
      let extractedContent = '';
      
      // Look for the first line that could be a title (starts with # or is the first substantial line)
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('#')) {
          extractedTitle = line.replace(/^#+\s*/, '').trim();
          extractedContent = lines.slice(i + 1).join('\n').trim();
          break;
        } else if (!extractedTitle && line.length > 10) {
          // Use first substantial line as title if no markdown heading found
          extractedTitle = line;
          extractedContent = lines.slice(i + 1).join('\n').trim();
          break;
        }
      }
      
      // Fallback if no proper title found
      if (!extractedTitle) {
        extractedTitle = `${gameTitle} - Game Review`;
        extractedContent = content;
      }
      
      console.log('Extracted title:', extractedTitle);
      console.log('Extracted content:', extractedContent);
      
      onContentGenerated(extractedTitle, extractedContent);
      toast.success('Blog post generated successfully!');
    } catch (error) {
      console.error('Failed to generate blog post:', error);
      toast.error('Failed to generate blog post');
    }
  };

  const handleGenerateCustom = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    try {
      console.log('Generating custom content with prompt:', prompt);
      const content = await generateCustomContent(prompt);
      
      console.log('Generated custom content:', content);
      setGeneratedContent(content);
      
      // Extract title and content from generated text
      const lines = content.split('\n').filter(line => line.trim());
      let extractedTitle = '';
      let extractedContent = '';
      
      // Look for the first line that could be a title
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('#')) {
          extractedTitle = line.replace(/^#+\s*/, '').trim();
          extractedContent = lines.slice(i + 1).join('\n').trim();
          break;
        } else if (!extractedTitle && line.length > 10) {
          extractedTitle = line;
          extractedContent = lines.slice(i + 1).join('\n').trim();
          break;
        }
      }
      
      // Fallback if no proper title found
      if (!extractedTitle) {
        extractedTitle = 'AI Generated Post';
        extractedContent = content;
      }
      
      console.log('Extracted title:', extractedTitle);
      console.log('Extracted content:', extractedContent);
      
      onContentGenerated(extractedTitle, extractedContent);
      toast.success('Content generated successfully!');
    } catch (error) {
      console.error('Failed to generate content:', error);
      toast.error('Failed to generate content');
    }
  };

  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent);
      setCopied(true);
      toast.success('Content copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy content');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            AI Blog Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Generate from Game */}
          <div className="space-y-2">
            <Label htmlFor="gameTitle">Generate Blog Post from Game</Label>
            <div className="flex gap-2">
              <Input
                id="gameTitle"
                placeholder="Enter game title (e.g., Super Mario Bros)"
                value={gameTitle}
                onChange={(e) => setGameTitle(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleGenerateFromGame}
                disabled={isLoading}
                className="min-w-[120px]"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4 mr-2" />
                )}
                Generate
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          {/* Custom Prompt */}
          <div className="space-y-2">
            <Label htmlFor="customPrompt">Custom Prompt</Label>
            <Textarea
              id="customPrompt"
              placeholder="Write a blog post about the latest gaming trends, or describe a specific game review you'd like to create..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
            />
            <Button 
              onClick={handleGenerateCustom}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Generate Custom Content
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Generated Content Preview */}
      {generatedContent && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Generated Content Preview
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyContent}
                className="flex items-center gap-2"
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm">{generatedContent}</pre>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIBlogGenerator;
